import React, { useEffect, useState, useCallback, useRef } from 'react';
import { fetchTooLateHistory, addTodoNote } from "../../service/api/orders";

const T2 = {
    bg: '#f4f5f7', white: '#ffffff', border: '#e2e4e9',
    text: '#1a1d23', muted: '#8b91a1', subtle: '#f8f9fb',
    blue: '#2563eb', blueB: '#dbeafe',
    green: '#15803d', greenB: '#dcfce7',
    red: '#dc2626', redB: '#fee2e2',
    amber: '#b45309', amberB: '#fef3c7',
};

const SEGMENT_LABELS = {
    placed_to_accept:   'Placed → Accept',
    accept_to_assign:   'Accept → Assign',
    assign_to_arrived:  'Assign → Arrived',
    waiting_restaurant: 'Waiting at Rest.',
    pickup_to_delivery: 'Pickup → Delivery',
};

const PRIORITY_OPTIONS = [
    { value: 'low',    label: 'Low',    color: T2.green, bg: T2.greenB },
    { value: 'medium', label: 'Medium', color: T2.amber, bg: T2.amberB },
    { value: 'high',   label: 'High',   color: T2.red,   bg: T2.redB   },
];

function NoteDisplay({ note }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontSize: '0.72rem', fontWeight: 700,
                background: T2.greenB, color: T2.green,
                border: `1px solid ${T2.green}44`,
                borderRadius: 6, padding: '3px 8px',
            }}>✓ Note Added</span>
            <span style={{
                fontSize: '0.72rem', color: T2.muted,
                maxWidth: 180, whiteSpace: 'normal', lineHeight: 1.4,
            }}>{note}</span>
        </div>
    );
}

function AddNoteDialog({ order, onClose, onSaved }) {
    const [note,     setNote]     = useState('');
    const [priority, setPriority] = useState('medium');
    const [saving,   setSaving]   = useState(false);
    const [error,    setError]    = useState(null);
    const textRef = useRef(null);

    useEffect(() => { textRef.current?.focus(); }, []);

    const handleSave = async () => {
        if (!note.trim()) { setError('Please enter a reason.'); return; }
        setSaving(true); setError(null);
        try {
            await addTodoNote({ data: note.trim(), priority, order_id: order.order_id });
            onSaved(order.unique_order_id, note.trim()); // ✅ fixed: actually call onSaved
            onClose();
        } catch (e) {
            setError('Failed to save. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            onClick={(e) => e.target === e.currentTarget && onClose()}
            style={{
                position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 10001, padding: 16,
            }}
        >
            <div style={{
                background: T2.white, borderRadius: 14, width: '100%', maxWidth: 420,
                boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden',
            }}>
                <div style={{
                    padding: '16px 20px', borderBottom: `1px solid ${T2.border}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <div>
                        <div style={{ fontSize: 14, fontWeight: 800, color: T2.text }}>Add Reason</div>
                        <div style={{ fontSize: 11, color: T2.muted, marginTop: 2 }}>
                            #{order.unique_order_id?.slice(-8)} · {order.restaurant_name}
                        </div>
                    </div>
                    <button onClick={onClose} style={{
                        background: T2.subtle, border: 'none', borderRadius: '50%',
                        width: 28, height: 28, cursor: 'pointer', color: T2.muted,
                        fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>✕</button>
                </div>

                <div style={{
                    margin: '14px 20px 0', padding: '10px 12px',
                    background: T2.redB, borderRadius: 8, border: `1px solid ${T2.red}33`,
                }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: T2.red, marginBottom: 6 }}>
                        ⏱ Total delay: {order.total_time}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {Object.entries(order.segments || {}).map(([key, val]) => {
                            const display = val?.display ?? (typeof val === 'string' ? val : null);
                            if (!display) return null;
                            return (
                                <span key={key} style={{
                                    fontSize: 10, fontWeight: 600,
                                    background: '#fff', color: T2.text,
                                    border: `1px solid ${T2.border}`,
                                    borderRadius: 4, padding: '2px 6px',
                                }}>
                                    {SEGMENT_LABELS[key] ?? key}: {display}
                                </span>
                            );
                        })}
                    </div>
                </div>

                <div style={{ padding: '14px 20px 0' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: T2.muted, marginBottom: 7, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Priority
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        {PRIORITY_OPTIONS.map(p => (
                            <button key={p.value} onClick={() => setPriority(p.value)} style={{
                                flex: 1, padding: '6px 0', borderRadius: 7, cursor: 'pointer',
                                fontSize: 12, fontWeight: 700,
                                background: priority === p.value ? p.bg : T2.subtle,
                                color: priority === p.value ? p.color : T2.muted,
                                border: priority === p.value ? `1.5px solid ${p.color}55` : `1px solid ${T2.border}`,
                                transition: 'all .12s',
                            }}>{p.label}</button>
                        ))}
                    </div>
                </div>

                <div style={{ padding: '14px 20px 0' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: T2.muted, marginBottom: 7, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Reason / Note
                    </div>
                    <textarea
                        ref={textRef}
                        value={note}
                        onChange={e => { setNote(e.target.value); setError(null); }}
                        placeholder="e.g. Driver had trouble finding the location…"
                        rows={3}
                        style={{
                            width: '100%', borderRadius: 8, padding: '10px 12px',
                            border: `1px solid ${error ? T2.red : T2.border}`,
                            fontSize: 13, color: T2.text, resize: 'vertical',
                            outline: 'none', fontFamily: 'inherit',
                            background: error ? T2.redB : T2.white,
                            boxSizing: 'border-box',
                        }}
                    />
                    {error && <div style={{ fontSize: 11, color: T2.red, marginTop: 4 }}>{error}</div>}
                </div>

                <div style={{ padding: '14px 20px 20px', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={{
                        padding: '8px 18px', borderRadius: 8, border: `1px solid ${T2.border}`,
                        background: T2.subtle, color: T2.muted, fontWeight: 600, fontSize: 13, cursor: 'pointer',
                    }}>Cancel</button>
                    <button onClick={handleSave} disabled={saving} style={{
                        padding: '8px 20px', borderRadius: 8, border: 'none',
                        background: saving ? T2.muted : T2.blue,
                        color: '#fff', fontWeight: 700, fontSize: 13,
                        cursor: saving ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', gap: 6, opacity: saving ? 0.7 : 1,
                    }}>
                        {saving ? 'Saving…' : 'Save Reason'}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ── History Tab ──────────────────────────────────────────────────────────────
function HistoryTab() {
    const [rows,     setRows]     = useState([]);
    const [page,     setPage]     = useState(1);
    const [hasMore,  setHasMore]  = useState(true);
    const [loading,  setLoading]  = useState(false);
    const sentinelRef = useRef(null);

    const loadPage = useCallback(async (pageNum) => {
        if (loading) return;
        setLoading(true);
        try {
            const res = await fetchTooLateHistory(pageNum);
            const incoming = res?.data ?? [];
            setRows(prev => pageNum === 1 ? incoming : [...prev, ...incoming]);
            setHasMore(res?.has_more ?? false);
            setPage(pageNum);
        } catch (e) {
            console.error('History load failed', e);
        } finally {
            setLoading(false);
        }
    }, [loading]);

    // initial load
    useEffect(() => { loadPage(1); }, []); // eslint-disable-line

    // infinite scroll via IntersectionObserver
    useEffect(() => {
        if (!sentinelRef.current) return;
        const obs = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loading) {
                    loadPage(page + 1);
                }
            },
            { threshold: 0.1 }
        );
        obs.observe(sentinelRef.current);
        return () => obs.disconnect();
    }, [hasMore, loading, page, loadPage]);

    if (!loading && rows.length === 0) return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, color: T2.muted, gap: 10 }}>
            <span style={{ fontSize: '2rem' }}>📭</span>
            <div style={{ fontSize: 14, fontWeight: 700 }}>No history yet</div>
            <div style={{ fontSize: 12 }}>Past too-late orders will appear here.</div>
        </div>
    );

    return (
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
                <thead>
                <tr style={{ background: '#f8f8f8', borderBottom: '1px solid #e4e4e7' }}>
                    {['#Order', 'Date', 'Restaurant', 'Delivery Guy', 'Total Time', 'Note'].map((h, i) => (
                        <th key={i} style={{
                            padding: '10px 14px', textAlign: 'left',
                            fontSize: '0.68rem', fontWeight: 700,
                            textTransform: 'uppercase', letterSpacing: '0.06em',
                            color: '#52525b', whiteSpace: 'nowrap',
                        }}>{h}</th>
                    ))}
                </tr>
                </thead>
                <tbody>
                {rows.map((o, i) => (
                    <tr key={o.unique_order_id} style={{
                        background: i % 2 === 0 ? '#fff' : '#fafafa',
                        borderBottom: '1px solid #f1f5f9',
                    }}>
                        <td style={{padding: '12px 14px', whiteSpace: 'nowrap'}}>
                            <a href={`/order/${o.unique_order_id}`} target="_blank" rel="noopener noreferrer"
                               style={{fontWeight: 700, color: T2.blue, fontSize: '0.8rem', textDecoration: 'none'}}>
                                #{o.unique_order_id?.slice(-8)}
                            </a>
                        </td>
                        <td style={{padding: '12px 14px', fontSize: '0.8rem', color: T2.muted, whiteSpace: 'nowrap'}}>
                            {o.placed_at ?? '—'}
                        </td>
                        <td style={{padding: '12px 14px', fontSize: '0.82rem', color: T2.text, maxWidth: 160}}>
                            {o.restaurant_name}
                        </td>
                        <td style={{padding: '12px 14px', fontSize: '0.82rem', color: T2.text, whiteSpace: 'nowrap'}}>
                            {o.delivery_guy_name ?? '—'}
                        </td>
                        <td style={{padding: '12px 14px', fontSize: '0.82rem', color: T2.text, whiteSpace: 'nowrap'}}>
                            {o.total_time ?? '—'}
                        </td>
                        <td style={{padding: '12px 14px'}}>
                            {o.has_todo_note
                                ? <NoteDisplay note={o.todo_note}/>
                                : <span style={{fontSize: '0.72rem', color: T2.muted}}>—</span>
                            }
                        </td>
                    </tr>
                ))}
                </tbody>
            </table>

            {/* Infinite scroll sentinel */}
            <div ref={sentinelRef} style={{padding: 16, display: 'flex', justifyContent: 'center' }}>
                {loading && (
                    <span style={{
                        display: 'inline-block', width: 18, height: 18,
                        border: '2px solid #e4e4e7', borderTopColor: T2.blue,
                        borderRadius: '50%', animation: 'tln-spin 0.7s linear infinite',
                    }} />
                )}
                {!loading && !hasMore && rows.length > 0 && (
                    <span style={{ fontSize: 12, color: T2.muted }}>— End of history —</span>
                )}
            </div>
        </div>
    );
}

// ── Main Modal ───────────────────────────────────────────────────────────────
export default function TooLateNotesModal({ open, setTooLateCount, onRefresh, orders, onClose }) {
    const [loading, setLoading] = useState(false);
    const [error,   setError]   = useState(null);
    const [notes,   setNotes]   = useState({});
    const [dialog,  setDialog]  = useState(null);
    const [tab,     setTab]     = useState('today'); // 'today' | 'history'
    const isMobile = window.innerWidth < 640;

    const load = useCallback(async () => {
        setLoading(true);
        try { await onRefresh(); }
        catch (e) { setError(e.message); }
        finally { setLoading(false); }
    }, [onRefresh]);

    useEffect(() => {
        if (open) { setNotes({}); setTab('today'); }
    }, [open]);

    useEffect(() => {
        const handler = (e) => e.key === 'Escape' && !dialog && onClose?.();
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [onClose, dialog]);

    if (!open) return null;

    const pendingOrders = orders.filter(o => !notes[o.unique_order_id] && !o.has_todo_note);
    const doneOrders    = orders.filter(o =>  notes[o.unique_order_id] ||  o.has_todo_note);
    const pendingCount  = pendingOrders.length;

    const handleSaved = (uniqueOrderId, noteText) => {
        setNotes(prev => ({ ...prev, [uniqueOrderId]: noteText }));
        setTooLateCount(prev => Math.max(0, prev - 1)); // ✅ badge count decrements
    };

    const TAB_STYLE = (active) => ({
        padding: '6px 16px', borderRadius: 8, border: 'none',
        fontSize: 13, fontWeight: 700, cursor: 'pointer',
        background: active ? T2.blue : 'transparent',
        color: active ? '#fff' : T2.muted,
        transition: 'all .15s',
    });

    return (
        <>
            <div
                onClick={(e) => e.target === e.currentTarget && !dialog && onClose?.()}
                style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: isMobile ? 'flex-end' : 'center',
                    justifyContent: 'center',
                    zIndex: 9998, padding: isMobile ? 0 : 16,
                }}
            >
                <div style={{
                    background: '#fff',
                    borderRadius: isMobile ? '16px 16px 0 0' : 16,
                    width: '100%', maxWidth: isMobile ? '100%' : 900,
                    maxHeight: isMobile ? '90vh' : '82vh',
                    display: 'flex', flexDirection: 'column', overflow: 'hidden',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                }}>
                    {isMobile && (
                        <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 10, paddingBottom: 2 }}>
                            <div style={{ width: 36, height: 4, borderRadius: 2, background: '#d4d4d8' }} />
                        </div>
                    )}

                    {/* Header */}
                    <div style={{
                        padding: '14px 20px', borderBottom: `1px solid ${T2.border}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        flexShrink: 0, background: T2.white,
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span style={{ fontSize: 18 }}>⏰</span>
                            <div>
                                <div style={{ fontSize: 14, fontWeight: 800, color: T2.text }}>
                                    Too Late Orders — Add Reasons
                                </div>
                                <div style={{ fontSize: 11, color: T2.muted, marginTop: 1 }}>
                                    Today · {orders.length} orders ·{' '}
                                    <span style={{ color: pendingCount > 0 ? T2.red : T2.green, fontWeight: 700 }}>
                                        {pendingCount} pending reason{pendingCount !== 1 ? 's' : ''}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {/* Tab switcher */}
                            <div style={{
                                display: 'flex', gap: 4, background: T2.subtle,
                                borderRadius: 10, padding: 3, border: `1px solid ${T2.border}`,
                            }}>
                                <button style={TAB_STYLE(tab === 'today')}   onClick={() => setTab('today')}>Today</button>
                                <button style={TAB_STYLE(tab === 'history')} onClick={() => setTab('history')}>History</button>
                            </div>
                            <button onClick={onClose} style={{
                                background: T2.subtle, border: 'none', borderRadius: '50%',
                                width: 30, height: 30, cursor: 'pointer', color: T2.muted,
                                fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>✕</button>
                        </div>
                    </div>

                    {/* Body */}
                    {tab === 'history' ? <HistoryTab /> : (
                        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'auto' }}>
                            {loading && (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: T2.muted, gap: 10 }}>
                                    <span style={{
                                        display: 'inline-block', width: 16, height: 16,
                                        border: '2px solid #e4e4e7', borderTopColor: T2.blue,
                                        borderRadius: '50%', animation: 'tln-spin 0.7s linear infinite',
                                    }} />
                                    Loading…
                                </div>
                            )}
                            {error && (
                                <div style={{ padding: 24, color: T2.red, textAlign: 'center', fontSize: 14 }}>{error}</div>
                            )}
                            {!loading && !error && orders.length === 0 && (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, color: T2.muted, gap: 10 }}>
                                    <span style={{ fontSize: '2rem' }}>✅</span>
                                    <div style={{ fontSize: 14, fontWeight: 700 }}>All caught up!</div>
                                    <div style={{ fontSize: 12 }}>No too-late orders without notes today.</div>
                                </div>
                            )}
                            {!loading && !error && orders.length > 0 && (
                                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 700 }}>
                                    <thead>
                                    <tr style={{ background: '#f8f8f8', borderBottom: '1px solid #e4e4e7' }}>
                                        {['#Order', 'Placed At', 'Restaurant', 'Delivery Guy', 'Total Delay', 'Most Delay At', 'Action'].map((h, i) => (
                                            <th key={i} style={{
                                                padding: '10px 14px', textAlign: 'left',
                                                fontSize: '0.68rem', fontWeight: 700,
                                                textTransform: 'uppercase', letterSpacing: '0.06em',
                                                color: '#52525b', whiteSpace: 'nowrap',
                                            }}>{h}</th>
                                        ))}
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {pendingOrders.map((o, i) => (
                                        <tr key={o.unique_order_id} style={{
                                            background: i % 2 === 0 ? '#fff' : '#fafafa',
                                            borderBottom: '1px solid #f1f5f9',
                                        }}>
                                            <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                                                <a href={`/order/${o.unique_order_id}`} target="_blank" rel="noopener noreferrer"
                                                   style={{ fontWeight: 700, color: T2.blue, fontSize: '0.8rem', textDecoration: 'none' }}>
                                                    #{o.unique_order_id?.slice(-8)}
                                                </a>
                                            </td>
                                            <td style={{ padding: '12px 14px', fontSize: '0.8rem', color: T2.muted, whiteSpace: 'nowrap' }}>{o.placed_at ?? '—'}</td>
                                            <td style={{ padding: '12px 14px', fontSize: '0.82rem', color: T2.text, maxWidth: 160 }}>{o.restaurant_name}</td>
                                            <td style={{ padding: '12px 14px', fontSize: '0.82rem', color: T2.text, whiteSpace: 'nowrap' }}>{o.delivery_guy_name ?? '—'}</td>
                                            <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                                                    <span style={{ fontWeight: 700, fontSize: '0.78rem', color: T2.red, background: T2.redB, padding: '3px 8px', borderRadius: 5, border: `1px solid ${T2.red}33` }}>
                                                        {o.total_time}
                                                    </span>
                                            </td>
                                            <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                                                    <span style={{ fontSize: '0.72rem', fontWeight: 700, background: T2.amberB, color: T2.amber, padding: '2px 7px', borderRadius: 5, border: `1px solid ${T2.amber}44` }}>
                                                        {SEGMENT_LABELS[o.most_delay_at] ?? o.most_delay_at ?? '—'}
                                                    </span>
                                            </td>
                                            <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                                                <button onClick={() => setDialog(o)} style={{
                                                    display: 'inline-flex', alignItems: 'center', gap: 5,
                                                    fontSize: '0.75rem', fontWeight: 700,
                                                    color: '#fff', background: T2.blue,
                                                    border: 'none', borderRadius: 6,
                                                    padding: '5px 12px', cursor: 'pointer',
                                                }}>+ Add Reason</button>
                                            </td>
                                        </tr>
                                    ))}

                                    {pendingOrders.length > 0 && doneOrders.length > 0 && (
                                        <tr><td colSpan={7} style={{ padding: 0 }}>
                                            <div style={{ borderTop: '2px dashed #d1fae5', margin: '4px 0', position: 'relative' }}>
                                                    <span style={{
                                                        position: 'absolute', top: -10, left: 14,
                                                        background: T2.greenB, color: T2.green,
                                                        fontSize: 10, fontWeight: 800,
                                                        padding: '1px 8px', borderRadius: 4, letterSpacing: '0.05em',
                                                    }}>NOTED ({doneOrders.length})</span>
                                            </div>
                                        </td></tr>
                                    )}

                                    {doneOrders.map((o, i) => {
                                        const savedNote = notes[o.unique_order_id] ?? o.todo_note ?? null;
                                        return (
                                            <tr key={o.unique_order_id} style={{
                                                background: i % 2 === 0 ? '#f0fdf4' : '#f7fef9',
                                                borderBottom: '1px solid #f1f5f9', opacity: 0.85,
                                            }}>
                                                <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                                                    <a href={`/order/${o.unique_order_id}`} target="_blank" rel="noopener noreferrer"
                                                       style={{ fontWeight: 700, color: T2.blue, fontSize: '0.8rem', textDecoration: 'none' }}>
                                                        #{o.unique_order_id?.slice(-8)}
                                                    </a>
                                                </td>
                                                <td style={{ padding: '12px 14px', fontSize: '0.8rem', color: T2.muted, whiteSpace: 'nowrap' }}>{o.placed_at ?? '—'}</td>
                                                <td style={{ padding: '12px 14px', fontSize: '0.82rem', color: T2.text, maxWidth: 160 }}>{o.restaurant_name}</td>
                                                <td style={{ padding: '12px 14px', fontSize: '0.82rem', color: T2.text, whiteSpace: 'nowrap' }}>{o.delivery_guy_name ?? '—'}</td>
                                                <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                                                        <span style={{ fontWeight: 700, fontSize: '0.78rem', color: T2.red, background: T2.redB, padding: '3px 8px', borderRadius: 5, border: `1px solid ${T2.red}33` }}>
                                                            {o.total_time}
                                                        </span>
                                                </td>
                                                <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                                                        <span style={{ fontSize: '0.72rem', fontWeight: 700, background: T2.amberB, color: T2.amber, padding: '2px 7px', borderRadius: 5, border: `1px solid ${T2.amber}44` }}>
                                                            {SEGMENT_LABELS[o.most_delay_at] ?? o.most_delay_at ?? '—'}
                                                        </span>
                                                </td>
                                                <td style={{ padding: '12px 14px' }}>
                                                    <NoteDisplay note={savedNote} />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}

                    {/* Footer — only show on today tab */}
                    {tab === 'today' && (
                        <div style={{
                            padding: isMobile ? '10px 16px 22px' : '10px 20px',
                            borderTop: `1px solid ${T2.border}`,
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            flexShrink: 0, background: T2.white,
                        }}>
                            <span style={{ fontSize: '0.72rem', color: T2.muted }}>Today's too-late orders only</span>
                            <button onClick={load} style={{
                                fontSize: '0.78rem', fontWeight: 700, color: T2.blue,
                                background: T2.blueB, border: 'none', padding: '6px 14px',
                                borderRadius: 7, cursor: 'pointer',
                            }}>↻ Refresh</button>
                        </div>
                    )}
                </div>
            </div>

            {dialog && (
                <AddNoteDialog
                    order={dialog}
                    onClose={() => setDialog(null)}
                    onSaved={handleSaved}
                />
            )}

            <style>{`@keyframes tln-spin { to { transform: rotate(360deg); } }`}</style>
        </>
    );
}